// Pocket Accountant: builds a P&L statement in the browser, nothing is saved or sent.
if (!customElements.get('pocket-accountant')) {
  const GROUPS = ['income', 'cogs', 'expenses'];

  // Placeholder numbers for a small online seller's month, not a real business.
  const EXAMPLE = {
    business: 'Sample Shop LLC',
    'sales-amazon': 22400,
    'sales-shopify': 4850,
    'sales-etsy': 1320,
    'refunds-and-returns': 410,
    'cost-of-goods-sold': 9200,
    'advertising-marketing': 3600,
    'bank-charges': 15,
    'contract-labor': 1200,
    'dues-subscriptions': 49,
    insurance: 140,
    'legal-accounting-fees': 250,
    'marketplace-seller-fees': 4150,
    'merchant-fees': 180,
    'office-expenses': 160,
    shipping: 1950,
    'software-web-hosting': 420,
    'telephone-internet': 120,
  };

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  // Accounting format to cents, with a minus sign for negatives, like the CSV export.
  const amount = (value) => {
    const text = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return value < 0 ? `-${text}` : text;
  };

  const longDate = (value) => {
    const parts = String(value || '').split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => !part)) return '';
    return new Date(parts[0], parts[1] - 1, parts[2]).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const csvCell = (value) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

  class PocketAccountant extends HTMLElement {
    connectedCallback() {
      this.tools = window.BDTTools;
      this.currency = this.dataset.currency;
      this.form = this.querySelector('[data-form]');
      this.template = this.querySelector('[data-line-template]');

      this.form.addEventListener('submit', (event) => event.preventDefault());
      this.form.addEventListener('input', () => this.update());
      this.form.addEventListener('change', (event) => this.tidy(event.target));
      this.form.addEventListener('reset', () =>
        setTimeout(() => {
          this.querySelectorAll('[data-custom-line]').forEach((line) => line.remove());
          this.update();
        })
      );
      this.form.addEventListener('click', (event) => {
        const add = event.target.closest('[data-add]');
        if (add) this.addLine(add.dataset.add);
        const remove = event.target.closest('[data-remove]');
        if (remove) {
          remove.closest('[data-custom-line]').remove();
          this.update();
        }
      });

      const example = this.querySelector('[data-example]');
      if (example) example.addEventListener('click', () => this.fill(EXAMPLE));

      this.querySelector('[data-download]').addEventListener('click', () => this.download());
      this.querySelector('[data-print]').addEventListener('click', () => window.print());

      const copy = this.querySelector('[data-copy]');
      if (copy) {
        copy.addEventListener('click', () => this.tools.copy(this.querySelector('[data-out="prompt"]').value, copy));
      }

      this.update();
    }

    out(name) {
      return this.querySelector(`[data-out="${name}"]`);
    }

    money(value) {
      return this.tools.money(value, this.currency);
    }

    tidy(input) {
      if (!input || !(input.dataset.field || 'customAmount' in input.dataset)) return;
      const value = this.tools.readNumber(input);
      input.value = value ? value.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '';
    }

    fill(values) {
      this.form.reset();
      setTimeout(() => {
        Object.keys(values).forEach((key) => {
          const input = this.form.querySelector(`[data-field="${key}"], [data-detail="${key}"]`);
          if (!input) return;
          input.value = typeof values[key] === 'number' ? values[key].toLocaleString('en-US') : values[key];
        });
        const today = new Date();
        const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        const iso = (date) =>
          `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        this.form.querySelector('[data-detail="start"]').value = iso(start);
        this.form.querySelector('[data-detail="end"]').value = iso(end);
        this.update();
      });
    }

    addLine(group) {
      const list = this.querySelector(`[data-custom-list="${group}"]`);
      const line = this.template.content.firstElementChild.cloneNode(true);
      const id = `${this.id || 'pa'}-custom-${Date.now()}`;
      line.querySelector('[data-custom-name]').id = `${id}-name`;
      line.querySelector('[data-name-label]').htmlFor = `${id}-name`;
      line.querySelector('[data-custom-amount]').id = `${id}-amount`;
      line.querySelector('[data-amount-label]').htmlFor = `${id}-amount`;
      list.append(line);
      line.querySelector('[data-custom-name]').focus();
    }

    // Returns { income: [{ name, value }], cogs: [...], expenses: [...] } with zero lines dropped.
    read() {
      const lines = {};
      GROUPS.forEach((group) => {
        const fieldset = this.form.querySelector(`[data-group="${group}"]`);
        const items = [];
        fieldset.querySelectorAll('[data-field]').forEach((input) => {
          const label = this.form.querySelector(`label[for="${input.id}"]`);
          let value = this.tools.readNumber(input);
          // Refunds are typed as a positive number and reduce income.
          if (input.dataset.field === 'refunds-and-returns') value = -value;
          items.push({ name: label ? label.textContent.trim() : input.dataset.field, value });
        });
        fieldset.querySelectorAll('[data-custom-line]').forEach((line) => {
          const name = line.querySelector('[data-custom-name]').value.trim();
          const value = this.tools.readNumber(line.querySelector('[data-custom-amount]'));
          items.push({ name: name || 'Other', value });
        });
        lines[group] = items.filter((item) => item.value !== 0);
      });
      lines.expenses.sort((a, b) => a.name.localeCompare(b.name));
      return lines;
    }

    calculate(lines) {
      const sum = (items) => items.reduce((total, item) => total + item.value, 0);
      const income = sum(lines.income);
      const cogs = sum(lines.cogs);
      const expenses = sum(lines.expenses);
      const gross = income - cogs;
      const net = gross - expenses;
      return { income, cogs, expenses, gross, net };
    }

    details() {
      const value = (key) => this.form.querySelector(`[data-detail="${key}"]`).value.trim();
      const start = longDate(value('start'));
      const end = longDate(value('end'));
      return {
        business: value('business') || 'Your Business',
        period: start && end ? `For the period ${start} to ${end}` : start ? `For the period starting ${start}` : '',
        end: value('end'),
      };
    }

    // One list of rows drives the on-screen statement and the CSV, so they always match.
    rows(lines, totals) {
      const rows = [];
      const block = (title, items, totalLabel, total) => {
        rows.push({ kind: 'header', label: title });
        items.forEach((item) => rows.push({ kind: 'line', label: item.name, value: item.value }));
        rows.push({ kind: 'total', label: totalLabel, value: total });
        rows.push({ kind: 'blank' });
      };
      block('Income', lines.income, 'Total Income', totals.income);
      block('Cost of Goods Sold', lines.cogs, 'Total Cost of Goods Sold', totals.cogs);
      rows.push({ kind: 'key', label: 'Gross Profit', value: totals.gross }, { kind: 'blank' });
      block('Operating Expenses', lines.expenses, 'Total Operating Expenses', totals.expenses);
      rows.push({ kind: 'key', label: 'Operating Income', value: totals.net }, { kind: 'blank' });
      rows.push({ kind: 'net', label: 'Net Income', value: totals.net });
      return rows;
    }

    update() {
      const lines = this.read();
      const totals = this.calculate(lines);
      const ready = GROUPS.some((group) => lines[group].length > 0);
      this.querySelector('[data-empty]').hidden = ready;
      this.querySelector('[data-filled]').hidden = !ready;
      if (!ready) return;

      this.current = { lines, totals, details: this.details(), rows: this.rows(lines, totals) };
      this.renderTiles(totals);
      this.renderStatement(this.current);
      this.tools.track('pocket-accountant');
    }

    renderTiles(totals) {
      const margin = (value) => (totals.income > 0 ? this.tools.percent(value / totals.income) : '—');
      this.out('income').textContent = this.money(totals.income);
      this.out('gross').textContent = this.money(totals.gross);
      this.out('gross-margin').textContent = `${margin(totals.gross)} gross margin`;
      this.out('net').textContent = this.money(totals.net);
      this.out('net-margin').textContent = `${margin(totals.net)} net margin`;
      this.querySelector('[data-tile="net"]').classList.toggle('is-loss', totals.net < 0);
    }

    renderStatement({ details, rows, totals }) {
      this.out('business').textContent = details.business;
      this.out('period').textContent = details.period;
      this.out('period').hidden = !details.period;

      const body = this.out('statement');
      body.replaceChildren(
        ...rows.map((row) => {
          const tr = el('tr', `bdt-pl__${row.kind}`);
          if (row.kind === 'blank') {
            const cell = el('td');
            cell.colSpan = 3;
            tr.append(cell);
            return tr;
          }
          const head = el('th', null, row.label);
          head.scope = 'row';
          tr.append(head);
          if (row.kind === 'header') {
            tr.append(el('td'), el('td'));
          } else {
            tr.append(
              el('td', null, amount(row.value)),
              el('td', 'bdt-pl__share', totals.income > 0 ? this.tools.percent(row.value / totals.income) : '')
            );
          }
          return tr;
        })
      );
    }

    download() {
      if (!this.current) return;
      const { details, rows } = this.current;
      const lines = [
        ['Income Statement (Profit and Loss)', ''],
        [details.business, ''],
        [details.period, ''],
        ['', ''],
        ['Account', 'Amount'],
        ['', ''],
        ...rows.map((row) => (row.kind === 'blank' ? ['', ''] : [row.label, row.value === undefined ? '' : amount(row.value)])),
      ];
      const csv = lines.map((line) => line.map(csvCell).join(',')).join('\r\n');
      const link = document.createElement('a');
      link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const slug = details.business.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      link.download = `profit-and-loss-${slug || 'statement'}${details.end ? '-' + details.end : ''}.csv`;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
    }
  }

  customElements.define('pocket-accountant', PocketAccountant);
}
